import { extendTheme } from "@chakra-ui/react";
import { statTheme } from "components/themes/stat";
import { selectTheme } from "components/themes/select";
import colors from "utils/colors";

const theme = extendTheme({
  colors: {
    primary: colors.PRIMARY,
    secondary: colors.SECONDARY,
    gray: {
      25: colors.GRAY_25,
      50: colors.GRAY_50,
      100: colors.GRAY_100,
      200: colors.GRAY_200,
      300: colors.GRAY_300,
    },
  },
  fonts: {
    logo: `'Montserrat', sans-serif`,
    heading: `'Poppins', sans-serif`,
    body: `'Poppins', sans-serif`,
  },
  styles: {
    global: {
      html: {
        // height: "100%",
      },
      body: {
        bg: "#F8F8F8",
        color: "black",
        fontFamily: "body",
        // height: "100%",
      },
      heading: {
        fontFamily: "body",
      },
      button: {
        fontFamily: "body",
      },
      ".chakra-table__container": {
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #E2E8F0",
      },
    },
  },
  components: {
    // Steps,
    Text: {
      baseStyle: {
        color: "gray.300",
      },
    },
    Progress: {
      baseStyle: {
        track: {
          bg: "#E2DFE8",
        },
        filledTrack: {
          bg: "primary",
        },
      },
      variants: {
        stat: {
          track: {
            bg: "#EEEEEE",
          },
          filledTrack: {
            bg: "#45C14A",
          },
        },
        green: {
          track: {
            bg: "#F8F6FB",
            height: "12px",
            borderRadius: "12px",
          },
          filledTrack: {
            bg: "#90BE95",
            minWidth: "12px",
          },
        },
        checklist: {
          track: {
            bg: "#F8F6FB",
            height: "12px",
            borderRadius: "12px",
          },
          filledTrack: {
            bg: "#45C14A",
          },
        },
        calculator: {
          track: {
            bg: "#9747FF",
          },
          filledTrack: {
            bg: "#E9B93D",
          },
        },
      },
    },
    Link: {
      variants: {
        button: {
          background: "primary",
          borderWidth: "2px",
          borderColor: "primary",
          borderRadius: "7px",
          fontWeight: "600",
          fontSize: "14px",
          lineHeight: "150%",
          minHeight: "2.5rem",
          color: "white",
          paddingX: "1rem",
          _hover: {
            background: "primary",
          },
        },
      },
    },
    Button: {
      variants: {
        white: {
          background: "white",
          borderWidth: "1px",
          borderColor: "gray.100",
          borderRadius: "8px",
          padding: "8px 16px",
          color: "#6C6B6E",
          fontWeight: "500",
          lineHeight: "16px",
          minHeight: "fit-content",
          minWidth: "fit-content",
          _hover: {
            background: "gray.100",
          },
        },
        solid: {
          background: "primary",
          borderWidth: "2px",
          borderColor: "primary",
          borderRadius: "7px",
          fontWeight: "600",
          fontSize: "14px",
          lineHeight: "150%",
          color: "white",
          _hover: {
            background: "primary",
          },
        },
        capsourcesolid: {
          background: "#0066B1",
          borderWidth: "2px",
          borderColor: "#0066B1",
          borderRadius: "7px",
          fontWeight: "600",
          fontSize: "14px",
          lineHeight: "150%",
          color: "white",
          _hover: {
            background: "#4D8FFF",
          },
        },
        whiteIcon: {
          background: "white",
          width: "50px",
          height: "50px",
          borderRadius: "50%",
          padding: "0px",
          fontSize: "22px",
          color: "secondary",
          filter:
            "drop-shadow(0px 4px 4px rgba(204, 204, 204, 0.25)) drop-shadow(0px 0px 15px rgba(198, 198, 198, 0.25))",
        },
        outline: {
          borderWidth: "1px",
          borderColor: "primary",
          color: "primary",
          fontWeight: "600",
          fontSize: "14px",
          lineHeight: "150%",
          _hover: {
            background: "#E2DFE8",
          },
        },
        whiteIconOutline: {
          color: "primary",
          borderWidth: "1px",
          borderColor: "gray.50",
          borderRadius: "50%",
        },
        secondary: {
          color: "gray.300",
          borderWidth: "1.5px",
          borderColor: "#E0E0E0",
          fontSize: "14px",
          fontWeight: "600",
        },
        neutral: {
          color: "gray.300",
          borderColor: "gray.300",
          borderWidth: "1.5px",
        },
        ghost: {
          _hover: {
            background: "transparent",
          },
        },
        red: {
          textColor: "white",
          background: "#ff144de3",
          _hover: {
            background: colors.RED,
          },
        },
        "ghost-red": {
          color: "gray.300",
          fontSize: "sm",
          fontWeight: "bold",
          _hover: {
            color: colors.RED,
          },
        },
        "ghost-teal": {
          color: "gray.300",
          fontSize: "sm",
          fontWeight: "bold",
          _hover: {
            color: "teal.500",
          },
        },
      },
    },
    FormLabel: {
      baseStyle: {
        fontSize: "14px",
        fontWeight: "500",
        lineHeight: "150%",
      },
      variants: {
        onboarding: {
          fontSize: "14px",
          fontWeight: "500",
          lineHeight: "28px",
        },
        large: {
          fontSize: "17px",
          fontWeight: "600",
        },
        filter: {
          color: "#595959",
          mb: "0px",
        },
      },
    },
    Input: {
      baseStyle: {
        border: "1px solid #FF0000",
      },
      variants: {
        primary: {
          field: {
            bg: "white",
            borderWidth: "1.5px",
            borderColor: "gray.50",
            borderRadius: "7px",
            _invalid: {
              border: "1.5px solid red",
            },
          },
        },
        flushed: {
          field: {
            fontWeight: 500,
            fontSize: "17px",
            lineHeight: "15px",
            color: "secondary",
            borderBottom: "2px solid #E2DFE8",
            _focus: {
              borderWidth: "2px",
              borderColor: "primary",
            },
          },
        },
        outline: {
          field: {
            border: "1.5px solid red",
          },
        },
        cell: {
          field: {
            background: "#F9F8F8",
            borderRadius: "5px",
            padding: "5px 10px",
          },
        },
        scenario: {
          field: {
            padding: "6px 15px",
            borderRadius: "5px",
            background: "#F9F8F8",

            textAlign: "right",
            height: "36px",
            fontSize: "14px",
            color: "secondary",
            _disabled: {
              color: "gray.200",
              opacity: 1,
            },
          },
        },
      },
      defaultProps: {
        variant: null,
      },
    },
    NumberInput: {
      // baseStyle: {},
      variants: {
        primary: {
          field: {
            bg: "#FFFFFF",
            borderWidth: "1.5px",
            borderColor: "gray.50",
            borderRadius: "7px",
          },
        },
        flushed: {
          field: {
            fontWeight: 500,
            fontSize: "17px",
            lineHeight: "15px",
            color: "secondary",
            borderBottom: "2px solid #E2DFE8",
          },
        },
      },
      defaultProps: {
        variant: null,
      },
    },
    Textarea: {
      baseStyle: {
        borderWidth: "1.5px",
        borderColor: "gray.50",
        resize: "none",
      },
      variants: {
        primary: {
          field: {
            borderWidth: "1.5px",
            borderColor: "gray.50",
          },
        },
      },
      defaultProps: {
        variant: null,
      },
    },
    Badge: {
      baseStyle: {
        padding: "0px 12px",
        color: "white",
        background: "black",
        fontSize: "12px",
        fontWeight: "600",
        textTransform: "capitalize",
        borderRadius: "50px",
      },
      defaultProps: {
        variant: null,
      },
      variants: {
        neutral: {
          color: "#4A494B",
          background: "#E4E3E6",
        },
        green: {
          color: "#45C14A",
          background: "#ECF9ED",
        },
        yellow: {
          color: "#F2B821",
          background: "#FEF8E9",
        },
        red: {
          color: colors.RED,
          background: "#FFE8ED",
        },
        lightBlue: {
          color: "#168FD2",
          background: "#E5F6FF",
        },
        darkBlue: {
          color: "#2746E5",
          background: "#E5E9FF",
        },
        purple: {
          color: "#601DCB",
          background: "#601DCB15",
        },
      },
    },
    Table: {
      baseStyle: {
        th: {
          whiteSpace: "normal",
        },
        td: {
          whiteSpace: "normal",
        },
      },
      variants: {
        summary: {
          tr: {
            _odd: {
              background: "#EEEEEE",
            },
          },
          th: {
            background: "white",
          },
        },
        "loan-fields": {
          tr: {
            _odd: {
              background: "#F9F8F8",
              borderRadius: "6px",
              fontSize: "14px",
              lineHeight: "28px",
            },
          },
          td: {
            paddingLeft: "15px",
            paddingTop: "15px",
            paddingBottom: "15px",
            fontWeight: "500",
            fontSize: "14px",
            color: "black",
            borderRadius: "6px",
          },
        },
        "scenario-settings": {
          table: {
            background: "#F9F8F8",
          },
          tbody: {
            background: "#F9F8F8",
          },
          tr: {
            background: "#F9F8F8",
          },
          td: {
            background: "white",
            paddingLeft: "20px",
            paddingTop: "7px",
            paddingBottom: "7px",
            fontWeight: "400",
            fontSize: "14px",
            color: "black",
          },
        },
        simple: {
          border: "1px solid #E2E8F0",
          tbody: {
            tr: {
              _hover: {
                background: "gray.25",
              },

              _last: {
                td: {
                  border: "1px solid transparent",
                },
              },
            },
          },
          th: {
            textTransform: "capitalize",
            color: "gray.200",
            fontSize: "14px",
            fontWeight: "500",
            lineHeight: "24px",
            padding: "12px",
            verticalAlign: "middle",
          },
          td: {
            color: "#302E34",
            fontSize: "14px",
            fontWeight: "400",
            padding: "12px",
            lineHeight: "24px",
            verticalAlign: "middle",
          },
        },
        print: {
          th: {
            padding: "8px",
            textTransform: "capitalize",
            fontWeight: "600",
            color: "black",
            border: `1px solid ${colors.GRAY_200}`,
            textAlign: "center",
            verticalAlign: "middle",
          },
          td: {
            border: `1px solid ${colors.GRAY_200}`,
            padding: "8px",
            verticalAlign: "middle",
          },
        },
      },
    },
    Menu: {
      // ...menuTheme,
      baseStyle: {
        list: {
          // this will style the MenuList component
          py: "10px",
          px: "16px",
          borderRadius: "14px",
          boxShadow:
            "0px 4px 4px rgba(204, 204, 204, 0.25), 0px 0px 15px rgba(198, 198, 198, 0.25)",
          border: "none",
          bg: "white",
        },
        item: {
          // this will style the MenuItem and MenuItemOption components
          color: "black",
          py: "12px",
          fontSize: "14px",
          fontWeight: "500",
          borderRadius: "6px",
          _hover: {
            bg: "#F8F6FB",
          },
          _focus: {
            bg: "white",
          },
        },
      },
      variants: {
        dropdown: {
          button: {
            background: "white",
            color: "gray.300",
            borderWidth: "1.5px",
            borderColor: "gray.50",
            borderRadius: "7px",
            fontSize: "16px",
            padding: "9px 15px",
            textAlign: "left",
          },
        },
      },
      defaultProps: {
        variant: null,
      },
    },
    Popover: {
      variants: {
        responsive: {
          content: { width: "unset" },
        },
      },
    },
    Radio: {
      baseStyle: {
        control: {
          borderColor: "primary",
          _checked: {
            background: "white",
            borderColor: "primary",
            _hover: {
              background: "primary",
              borderColor: "primary",
            },
            _before: {
              background: "primary",
            },
          },
        },
      },
    },
    Select: selectTheme,
    Stat: statTheme,
    Card: {
      baseStyle: {
        container: {
          bg: "white",
          borderRadius: "12px",
          boxShadow: "0px 2px 8px 0px #00000014",
        },
        header: {
          fontWeight: 600,
          fontSize: "18px",
          lineHeight: "24px",
          color: "#302E34",
          padding: "16px",
        },
        body: {
          padding: "16px",
        },
        footer: {
          padding: "16px",
        },
      },
      variants: {
        widget: {},
      },
    },
    Tooltip: {
      baseStyle: {
        background: "#302E34",
        color: "white",
        borderRadius: "8px",
      },
    },
  },
});

export default theme;
